import { useEffect, useState } from 'react'
import styles from './AccessDetails.module.css'
import { apiJson, getActiveTenantId, type TokenSource } from '../../lib/api'

// What a permission set opens, and what a sign-in holds beyond it (D1, as
// approved by Kerrin 2026-10-01). Both read the server: the matrix is
// GET /api/permission-sets, the same definitions the auth guard enforces
// (services/permissions.js), so the panel can never show a grant the server
// does not apply. Capabilities and report scope are routes/memberships.js.

type Level = 'none' | 'read' | 'edit' | 'add'
interface PermissionSets {
  areas: { key: string; label: string; capabilities?: boolean }[]
  matrix: Record<string, Record<string, Level>>
  notes: Record<string, string>
}
const LEVEL_TEXT: Record<Level, string> = { none: 'No access', read: 'Read', edit: 'Edit', add: 'Add + edit' }

const isSets = (s: any): s is PermissionSets =>
  Array.isArray(s?.areas) && !!s?.matrix && typeof s.matrix === 'object' && !!s?.notes && typeof s.notes === 'object'
const isAccess = (d: any): d is MembershipAccess =>
  !!d?.membership && Array.isArray(d?.capabilities) && Array.isArray(d?.report_scopes)

// One read per workspace: the matrix changes only with a release, but it is the matrix the workspace has
// (without the Stock & Dispatch licence its columns are left out), so one workspace's answer is not
// kept for another.
let setsOnce: { tenant: string | null; read: Promise<PermissionSets> } | null = null
function permissionSets(user: TokenSource | null) {
  const tenant = getActiveTenantId()
  if (!setsOnce || setsOnce.tenant !== tenant) {
    const mine: { tenant: string | null; read: Promise<PermissionSets> } = {
      tenant,
      read: apiJson<unknown>(user, '/api/permission-sets').then((s) => {
        if (!isSets(s)) throw new Error('Could not read what the permission sets open')
        return s
      }).catch((e) => {
        if (setsOnce === mine) setsOnce = null
        throw e
      }),
    }
    setsOnce = mine
  }
  return setsOnce.read
}
export const forgetPermissionSets = () => {
  setsOnce = null
}

export function SetMatrix({ user, set }: { user: TokenSource | null; set: string }) {
  const [sets, setSets] = useState<PermissionSets | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    permissionSets(user)
      .then((s) => live && setSets(s))
      .catch((e) => live && setError(e?.message || 'Could not load what the permission sets open'))
    return () => {
      live = false
    }
  }, [user])
  if (error) return <div className={styles.note}>{error}</div>
  if (!sets) return <div className={styles.note}>Loading what {set} opens…</div>
  const levels = sets.matrix[set]
  if (!levels) return null
  const everyArea = sets.notes[`${set}.*`]
  return (
    <div className={styles.matrix} role="table" aria-label={`What ${set} opens`}>
      {sets.areas.map((a) => {
        const note = sets.notes[`${set}.${a.key}`]
        const level = a.capabilities ? null : levels[a.key] || 'none'
        return (
          <div key={a.key} role="row" className={styles.row}>
            <span role="rowheader" className={styles.area}>
              {a.label}
            </span>
            <span role="cell" className={level === 'none' ? styles.none : styles.level}>
              {level ? LEVEL_TEXT[level] : set === 'SYSADMIN' ? 'Dataphyll support access' : 'By named grant below'}
              {note && <span className={styles.levelNote}>{note}</span>}
            </span>
          </div>
        )
      })}
      {everyArea && <div className={styles.note}>{everyArea}</div>}
    </div>
  )
}

interface CapabilityRow {
  key: string
  label: string
  requires: string[]
  held: boolean
  in_effect: boolean
  grantable: boolean
  granted: { by: string | null; at: string } | null
}
export interface MembershipAccess {
  membership: { id: string; name: string | null; email: string; role: string; archived: boolean }
  self: boolean
  capability_source: 'grants' | 'dataphyll_support'
  // D2: the tenant's Stock & Dispatch state. Capabilities come back only
  // when it is licensed; an older server sends no state.
  module_state?: 'licensed' | 'not_licensed' | 'not_available' | 'unconfigured'
  capabilities: CapabilityRow[]
  report_scope: 'managed_groups' | 'tenant' | null
  report_scopes: string[]
}

const SCOPE_TEXT: Record<string, { label: string; text: string }> = {
  // tenant first: after a closing brace the copy check reads a key as prose.
  tenant: { label: 'Wider operational reporting', text: 'For an owner or grower: operational reports across the workspace. Never Wages.' },
  managed_groups: { label: 'Groups they manage', text: 'Reports cover the groups this person manages, and nothing else.' },
}

// Stock & Dispatch capabilities and a REPORTING sign-in's report scope. The
// capabilities are shown only while the tenant holds the module's licence
// (D2): on Grow, or before a product is set, they are absent; on PackRight
// without the licence one line says the grants are kept, not what they are.
// server answers only for someone holding grant_access (ADMIN, SYSADMIN);
// the caller renders this only for them. Every change is written by the
// server with who made it.
export function GrantsSection({ user, membershipId, onChanged }: { user: TokenSource | null; membershipId: string; onChanged?: () => void }) {
  const [data, setData] = useState<MembershipAccess | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setData(null)
    setError(null)
    apiJson<unknown>(user, `/api/memberships/${membershipId}/access`)
      .then((d) => {
        if (!live) return
        if (isAccess(d)) setData(d)
        else setError('Could not read this sign-in’s grants')
      })
      .catch((e) => live && setError(e?.message || 'Could not load this sign-in’s grants'))
    return () => {
      live = false
    }
  }, [user, membershipId])

  const change = async (key: string, path: string, method: 'POST' | 'PUT', body?: unknown) => {
    setBusy(key)
    setError(null)
    try {
      const next = await apiJson<unknown>(user, path, { method, body })
      if (!isAccess(next)) throw new Error('The change was sent, but its answer could not be read. Reopen the record to see what was saved.')
      setData(next)
      onChanged?.()
    } catch (e: any) {
      setError(e?.message || 'Nothing was saved')
    } finally {
      setBusy(null)
    }
  }

  if (!data) return error ? <div className={styles.error} role="alert">{error}</div> : <div className={styles.note}>Loading grants…</div>
  const support = data.capability_source === 'dataphyll_support'
  const byKey = Object.fromEntries(data.capabilities.map((c) => [c.key, c]))
  const state = data.module_state || 'licensed'
  return (
    <div className={styles.grants}>
      {state === 'not_licensed' && (
        <>
          <div className={styles.head}>Stock & Dispatch capabilities</div>
          <div className={styles.note} data-testid="caps-unlicensed">
            This workspace does not hold the Stock & Dispatch licence, so no capability is in effect. Any already given are kept, and apply again if the
            licence returns.
          </div>
        </>
      )}
      {state === 'licensed' && <div className={styles.head}>Stock & Dispatch capabilities</div>}
      {state !== 'licensed' ? null : support ? (
        <div className={styles.note}>SYSADMIN is Dataphyll support: it has every capability, and it is not given or taken here.</div>
      ) : (
        <>
          <div className={styles.note}>
            Given to named people only; ADMIN holds none by being ADMIN.{data.self ? ' You may give them to yourself: it is recorded.' : ''}
          </div>
          <ul className={styles.caps} aria-label="Stock & Dispatch capabilities">
            {data.capabilities.map((c) => {
              const needs = c.requires.filter((r) => !byKey[r]?.held).map((r) => byKey[r]?.label || r)
              const heldBy = data.capabilities.filter((o) => o.held && o.requires.includes(c.key)).map((o) => o.label)
              return (
                <li key={c.key} className={styles.cap}>
                  <span className={c.held ? styles.capOn : styles.capOff}>{c.label}</span>
                  {c.held && !c.in_effect && <span className={styles.levelNote}>Held, not in effect for {data.membership.role}</span>}
                  {c.held ? (
                    <button
                      type="button"
                      className={styles.linkBtn}
                      disabled={busy !== null || heldBy.length > 0}
                      title={heldBy.length ? `${heldBy.join(' and ')} ${heldBy.length === 1 ? 'needs' : 'need'} it` : undefined}
                      onClick={() => change(c.key, `/api/memberships/${membershipId}/capabilities/${c.key}/revoke`, 'POST')}
                    >
                      {busy === c.key ? 'Taking away…' : 'Take away'}
                    </button>
                  ) : c.grantable ? (
                    <button
                      type="button"
                      className={styles.linkBtn}
                      disabled={busy !== null || needs.length > 0}
                      title={needs.length ? `Needs ${needs.join(' and ')} first` : undefined}
                      onClick={() => change(c.key, `/api/memberships/${membershipId}/capabilities`, 'POST', { capability: c.key })}
                    >
                      {busy === c.key ? 'Giving…' : 'Give'}
                    </button>
                  ) : (
                    <span className={styles.levelNote}>{data.membership.role === 'REPORTING' ? 'REPORTING: View stock & orders at most' : 'Cannot be given'}</span>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
      {data.report_scope && (
        <>
          <div className={styles.head}>Reporting</div>
          <div className={styles.scopes} role="radiogroup" aria-label="Report scope">
            {data.report_scopes.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={data.report_scope === s}
                disabled={busy !== null || data.self}
                className={data.report_scope === s ? styles.scopeOn : styles.scope}
                onClick={() => data.report_scope !== s && change('scope', `/api/memberships/${membershipId}/report-scope`, 'PUT', { scope: s })}
              >
                <span className={styles.scopeLabel}>{SCOPE_TEXT[s]?.label || s}</span>
                <span className={styles.levelNote}>{SCOPE_TEXT[s]?.text}</span>
              </button>
            ))}
          </div>
          {data.self && <div className={styles.note}>Nobody changes their own reporting scope. Ask another ADMIN.</div>}
        </>
      )}
      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  )
}
